import { useState, useEffect, useCallback, useRef } from "react";

export const useGateSpot = (symbol) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [orderBook, setOrderBook] = useState({ asks: [], bids: [] });
  const [tickerData, setTickerData] = useState(null); // { lastPrice, volume24, baseVolume24, quoteVolume24, symbol }

  const wsRef = useRef(null);
  const orderBookRef = useRef({ asks: [], bids: [] });
  const hasInitialBook = useRef(false);
  const animationFrameRef = useRef(null);
  const deltaBufferRef = useRef([]);
  const lastUpdateId = useRef(null);

  // ---------------- Загрузка снапшота через REST ----------------
  const loadInitialSnapshot = useCallback(async () => {
    if (!symbol) return;
    try {
      const PROXY = `${process.env.REACT_APP_BACKEND_URL}/proxy`;
      const url = `${PROXY}/https://api.gateio.ws/api/v4/spot/order_book?currency_pair=${symbol}&limit=50&with_id=true`;
      const res = await fetch(url, {
        headers: {
          'ngrok-skip-browser-warning': 'true',
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      lastUpdateId.current = data.id;

      const asks = (data.asks || [])
        .map(([p, q]) => [String(p), String(q)])
        .filter(([, q]) => parseFloat(q) > 0)
        .sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]))
        .slice(0, 20);

      const bids = (data.bids || [])
        .map(([p, q]) => [String(p), String(q)])
        .filter(([, q]) => parseFloat(q) > 0)
        .sort((a, b) => parseFloat(b[0]) - parseFloat(a[0]))
        .slice(0, 20);

      orderBookRef.current = { asks, bids };
      hasInitialBook.current = true;
      setOrderBook({ asks, bids });

      console.log("✅ [GATE SPOT] Снапшот загружен:", lastUpdateId.current);

      // применяем накопленные дельты
      flushDeltaBuffer();
    } catch (e) {
      console.error("❌ [GATE SPOT] Ошибка загрузки снапшота:", e);
      setError(e?.message || "Ошибка загрузки стакана");
    }
  }, [symbol]);

  // ---------------- Подписка/отписка на тикеры (цена/объём) ----------------
  const subscribeTickers = useCallback((ws) => {
    ws.send(JSON.stringify({
      time: Date.now(),
      channel: "spot.tickers",
      event: "subscribe",
      payload: [symbol], // напр. "ETH_USDT"
    }));
    console.log("📡 [GATE SPOT] SUB TICKERS:", symbol);
  }, [symbol]);

  const unsubscribeTickers = useCallback((ws) => {
    try {
      ws?.send(JSON.stringify({
        time: Date.now(),
        channel: "spot.tickers",
        event: "unsubscribe",
        payload: [symbol],
      }));
      console.log("📴 [GATE SPOT] UNSUB TICKERS:", symbol);
    } catch {}
  }, [symbol]);

  // ---------------- Применение дельт стакана ----------------
  const applyDeltas = useCallback((updates, side) => {
    const book = [...orderBookRef.current[side]];

    updates.forEach(([price, quantity]) => {
      const priceNum = parseFloat(price);
      const qtyNum = parseFloat(quantity);
      const index = book.findIndex(([p]) => parseFloat(p) === priceNum);

      if (qtyNum === 0) {
        if (index !== -1) book.splice(index, 1);
      } else {
        if (index !== -1) {
          book[index] = [price, quantity];
        } else {
          book.push([price, quantity]);
        }
      }
    });

    orderBookRef.current[side] =
      side === "asks"
        ? book.sort((a, b) => parseFloat(a[0]) - parseFloat(b[0])).slice(0, 20)
        : book.sort((a, b) => parseFloat(b[0]) - parseFloat(a[0])).slice(0, 20);

    if (animationFrameRef.current)
      cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = requestAnimationFrame(() => {
      setOrderBook({ ...orderBookRef.current });
    });
  }, []);

  const flushDeltaBuffer = useCallback(() => {
    if (!deltaBufferRef.current.length) return;
    console.log(`📦 [GATE SPOT] Применяем буфер (${deltaBufferRef.current.length})`);

    deltaBufferRef.current.forEach((msg) => {
      if (!msg || !msg.result) return;
      const { a = [], b = [] } = msg.result;
      if (a.length) applyDeltas(a, "asks");
      if (b.length) applyDeltas(b, "bids");
    });

    deltaBufferRef.current.length = 0;
  }, [applyDeltas]);

  // ---------------- Обработка сообщений WebSocket ----------------
  const handleMessage = useCallback(
    (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.event === "subscribe" || data.event === "unsubscribe") {
          console.log("✅ [GATE SPOT]", data.event, "ok:", data.channel);
          return;
        }

        // --- ТИКЕРЫ: последняя цена / объёмы (по доке result — объект) ---
        if (data.channel === "spot.tickers" && data.event === "update") {
          const r = data.result || {};
          const last = r.last ?? r.last_price ?? r.close;
          const baseVol = r.base_volume;
          const quoteVol = r.quote_volume;

          setTickerData({
            symbol: r.currency_pair || symbol,
            lastPrice: last != null ? String(last) : undefined,
            // Для UI обычно удобнее quote объём (в USDT)
            volume24: quoteVol != null ? String(quoteVol) : undefined,
            baseVolume24: baseVol != null ? String(baseVol) : undefined,
            quoteVolume24: quoteVol != null ? String(quoteVol) : undefined,
            high24h: r.high_24h,
            low24h: r.low_24h,
          });
          return;
        }

        // --- СТАКАН (Order Book V2) ---
        if (data.channel === "spot.obu" && data.event === "update") {
          const { full, a = [], b = [], u } = data.result;

          if (full) {
            console.log("📥 [GATE SPOT] FULL snapshot WS");
            orderBookRef.current = {
              asks: a.sort((x, y) => parseFloat(x[0]) - parseFloat(y[0])).slice(0, 20),
              bids: b.sort((x, y) => parseFloat(y[0]) - parseFloat(x[0])).slice(0, 20),
            };
            setOrderBook({ ...orderBookRef.current });
            lastUpdateId.current = u;
            hasInitialBook.current = true;
            flushDeltaBuffer();
            return;
          }

          // инкрементальные дельты
          if (!hasInitialBook.current) {
            deltaBufferRef.current.push(data);
            return;
          }

          if (u <= lastUpdateId.current) return; // старое обновление
          lastUpdateId.current = u;

          if (a.length) applyDeltas(a, "asks");
          if (b.length) applyDeltas(b, "bids");
        }
      } catch (e) {
        console.error("❌ [GATE SPOT] Ошибка парсинга:", e);
      }
    },
    [applyDeltas, flushDeltaBuffer, symbol]
  );

  // ---------------- Основной эффект ----------------
  useEffect(() => {
    if (!symbol) return;

    hasInitialBook.current = false;
    deltaBufferRef.current.length = 0;
    orderBookRef.current = { asks: [], bids: [] };
    setOrderBook({ asks: [], bids: [] });
    setTickerData(null);
    setError(null);

    const ws = new WebSocket("wss://api.gateio.ws/ws/v4/");
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      setError(null);

      // стакан
      const payload = [`ob.${symbol}.50`];
      ws.send(
        JSON.stringify({
          time: Date.now(),
          channel: "spot.obu",
          event: "subscribe",
          payload,
        })
      );
      console.log("📡 [GATE SPOT] Подписка OBU:", payload);

      // тикер
      subscribeTickers(ws);

      // начальный снапшот для быстрого рендера
      loadInitialSnapshot();
    };

    ws.onmessage = handleMessage;
    ws.onerror = (err) => {
      console.error("❌ [GATE SPOT] WS ошибка:", err);
      setError(err.message);
      setIsConnected(false);
    };
    ws.onclose = () => {
      console.log("🔌 [GATE SPOT] WS закрыт");
      setIsConnected(false);
    };

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        // корректная отписка
        unsubscribeTickers(ws);
        ws.send(
          JSON.stringify({
            time: Date.now(),
            channel: "spot.obu",
            event: "unsubscribe",
            payload: [`ob.${symbol}.50`],
          })
        );
        ws.close();
      }
      if (animationFrameRef.current)
        cancelAnimationFrame(animationFrameRef.current);
    };
  }, [symbol, handleMessage, loadInitialSnapshot, subscribeTickers, unsubscribeTickers]);

  return { isConnected, error, orderBook, tickerData };
};
